<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { fetchBookmarkData } from '@/api/bookmark'
import { useTheme } from '@/composables/useTheme'
import type { BookmarkItem } from '@/types'
import AppHeader from './components/AppHeader.vue'
import Sidebar from './components/Sidebar.vue'
import SearchBar from './components/SearchBar.vue'
import Footer from './components/Footer.vue'
import BookmarkCard from './components/BookmarkCard.vue'

const { init } = useTheme()

const state = ref<'loading' | 'content' | 'error'>('loading')
const error = ref('')

const bookmarks = ref<BookmarkItem[]>([])
const currentBookmarks = ref<BookmarkItem[]>([])
const breadcrumb = ref<BookmarkItem[]>([])
const sidebarOpen = ref(window.innerWidth >= 768)
const searchOpen = ref(false)
const searchQuery = ref('')
const lastSync = ref<number | null>(null)

const totalBookmarks = computed(() => countBookmarks(bookmarks.value))
const totalFolders = computed(() => countFolders(bookmarks.value))
const itemCount = computed(() => currentBookmarks.value.length)

// 当前路径
const currentPath = computed(() => {
  if (breadcrumb.value.length === 0) return '根目录'
  return breadcrumb.value.map(item => item.name).join(' / ')
})

async function loadData() {
  try {
    const data = await fetchBookmarkData()
    bookmarks.value = data.bookmarks
    currentBookmarks.value = data.bookmarks
    lastSync.value = data.timestamp
    state.value = 'content'
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加载失败'
    state.value = 'error'
  }
}

function countBookmarks(items: BookmarkItem[]): number {
  let count = 0
  items.forEach(item => {
    if (item.isFolder && item.children) {
      count += countBookmarks(item.children)
    } else {
      count++
    }
  })
  return count
}

function countFolders(items: BookmarkItem[]): number {
  let count = 0
  items.forEach(item => {
    if (item.isFolder) {
      count++
      if (item.children) {
        count += countFolders(item.children)
      }
    }
  })
  return count
}

function handleSearch(query: string) {
  searchQuery.value = query
  if (!query.trim()) {
    // 清空搜索时，返回当前文件夹内容
    if (breadcrumb.value.length === 0) {
      currentBookmarks.value = bookmarks.value
    } else {
      const currentFolder = breadcrumb.value[breadcrumb.value.length - 1]
      currentBookmarks.value = currentFolder.children || []
    }
  } else {
    currentBookmarks.value = filterBookmarks(currentBookmarks.value, query.toLowerCase())
  }
}

// 导航到文件夹
function navigateToFolder(bookmark: BookmarkItem) {
  if (bookmark.isFolder && bookmark.children) {
    breadcrumb.value.push(bookmark)
    currentBookmarks.value = bookmark.children
    searchQuery.value = ''
  }
}

// 返回上级
function navigateBack() {
  if (breadcrumb.value.length === 0) return

  breadcrumb.value.pop()
  if (breadcrumb.value.length === 0) {
    currentBookmarks.value = bookmarks.value
  } else {
    const currentFolder = breadcrumb.value[breadcrumb.value.length - 1]
    currentBookmarks.value = currentFolder.children || []
  }
  searchQuery.value = ''
}

function filterBookmarks(items: BookmarkItem[], query: string): BookmarkItem[] {
  const results: BookmarkItem[] = []
  items.forEach(item => {
    if (item.name.toLowerCase().includes(query)) {
      results.push(item)
    }
    if (item.url && item.url.toLowerCase().includes(query)) {
      if (!results.includes(item)) {
        results.push(item)
      }
    }
    if (item.isFolder && item.children) {
      const childrenResults = filterBookmarks(item.children, query)
      childrenResults.forEach(child => {
        if (!results.includes(child)) {
          results.push(child)
        }
      })
    }
  })
  return results
}

onMounted(() => {
  init()
  loadData()

  // 监听窗口大小变化，自动切换侧边栏状态
  window.addEventListener('resize', () => {
    sidebarOpen.value = window.innerWidth >= 768
  })
})
</script>

<template>
  <div class="min-h-screen bg-background flex flex-col">
    <AppHeader
      :breadcrumb="breadcrumb"
      @navigate-back="navigateBack"

      @toggle-sidebar="sidebarOpen = !sidebarOpen"
      @toggle-search="searchOpen = !searchOpen"
    />

    <SearchBar
      :is-open="searchOpen"
      @search="handleSearch"
      @close="searchOpen = false"
    />

    <div class="main-content flex flex-1">
      <Sidebar
        :is-open="sidebarOpen"
        :bookmarks="bookmarks"
        :total-bookmarks="totalBookmarks"
        :total-folders="totalFolders"
        :last-sync="lastSync"
        @toggle="sidebarOpen = !sidebarOpen"
        @navigate="navigateToFolder"
      />

      <main class="content-area flex-1 p-6">
        <div v-if="state === 'loading'" class="flex items-center justify-center py-20">
          <div class="text-center">
            <div class="w-10 h-10 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
            <p class="text-muted-foreground">加载中...</p>
          </div>
        </div>

        <div v-else-if="state === 'error'" class="flex items-center justify-center py-20">
          <div class="text-center">
            <i class="fas fa-exclamation-circle text-destructive text-5xl mb-4"></i>
            <p class="text-destructive mb-4">{{ error }}</p>
            <button class="btn-icon bg-primary text-primary-foreground" @click="loadData">
              重新加载
            </button>
          </div>
        </div>

        <div v-else>
          <div class="content-header flex items-center justify-between mb-6">
            <span class="text-muted-foreground">{{ itemCount }} 项</span>
          </div>

          <div class="bookmark-container flex flex-col gap-2">
            <BookmarkCard
              v-for="bookmark in currentBookmarks"
              :key="bookmark.id"
              :bookmark="bookmark"
              @navigate="navigateToFolder"
            />
          </div>
        </div>
      </main>
    </div>

    <Footer
      status="就绪"
      :current-path="currentPath"
    />
  </div>
</template>
